import { useEffect } from 'react';

/**
 * Blocks pinch- and double-tap-zoom for as long as an applicant screen is mounted,
 * then puts the page back exactly as it was.
 *
 * Why this exists on top of the CSS: `.sc-session-preview-page` already sets
 * `touch-action: manipulation` / `pan-y`, which stops zoom gestures that begin inside
 * the run. But on iOS Safari pinch-zoom is also a PAGE-level gesture the browser
 * handles above any element, so `touch-action` alone does not always catch it.
 *
 * Why it is a hook rather than a line in index.html: that file is shared with the
 * ADMIN app, and disabling zoom while someone reads a sessions table would be a real
 * accessibility regression (WCAG 1.4.4). Confining it to the applicant journey keeps
 * the app-like feel where it is wanted and zoom where it is needed.
 *
 * Three layers, because on iOS no single one is sufficient:
 *
 *  1. The viewport meta, installed by REPLACING the tag rather than assigning to
 *     `.content`. On a cold page load Safari has already committed its zoom
 *     configuration by the time a post-paint effect runs, and a mutated attribute is
 *     not reliably re-read — which is exactly why the FIRST screen of a run still
 *     pinch-zoomed while every screen after it (a client-side navigation, re-evaluated)
 *     did not. Re-inserting the node forces a re-parse.
 *  2. Safari's `gesture*` events, which fire for pinch specifically. Cancelling them
 *     blocks the gesture even in the cases where the meta is ignored. These events do
 *     not exist on other engines, so the listeners are simply inert there.
 *  3. Multi-touch `touchmove` where `scale` has moved off 1 — the same guard for
 *     engines that report scale but not `gesture*`. Single-finger scrolling never has
 *     a scale, so this cannot interfere with it.
 */
const NO_ZOOM =
  'width=device-width, initial-scale=1.0, maximum-scale=1.0, minimum-scale=1.0, user-scalable=no, viewport-fit=cover';

// Ref-counted so the two applicant routes can overlap. Navigating the welcome screen
// → the run unmounts one consumer and mounts another; React does not guarantee that
// the outgoing cleanup runs before the incoming effect, and if it ran after, it would
// restore zoom ON the run. Counting means the tag is only put back when the LAST
// applicant screen leaves, whatever the order.
let mountCount = 0;
let restoreContent: string | null = null;
let installedMeta: HTMLMetaElement | null = null;

function install(): void {
  const existing = document.querySelector<HTMLMetaElement>('meta[name="viewport"]');
  restoreContent = existing?.content ?? null;
  existing?.remove();

  const meta = document.createElement('meta');
  meta.name = 'viewport';
  meta.content = NO_ZOOM;
  document.head.appendChild(meta);
  installedMeta = meta;
}

function uninstall(): void {
  installedMeta?.remove();
  installedMeta = null;

  // Only re-add what was actually there. Inventing a default would silently
  // overwrite whatever index.html intended.
  if (restoreContent !== null) {
    const restored = document.createElement('meta');
    restored.name = 'viewport';
    restored.content = restoreContent;
    document.head.appendChild(restored);
    restoreContent = null;
  }
}

/** Cancels a pinch outright — see layer 2/3 above. */
function blockGesture(event: Event): void {
  event.preventDefault();
}

function blockPinchTouch(event: TouchEvent): void {
  // Keyed on WebKit's non-standard `scale`, NOT on `touches.length`. Two fingers on
  // the screen is not the same thing as a zoom — Android reports two touches during
  // ordinary two-thumb scrolling, so cancelling on the count would freeze scrolling
  // on a long form. A scale that has moved off 1 means the browser really is zooming.
  // Android's Chrome honours `maximum-scale` from the meta anyway, so it never needs
  // this layer; iOS is the platform that does.
  const scale = (event as TouchEvent & { scale?: number }).scale;
  if (scale !== undefined && scale !== 1) {
    event.preventDefault();
  }
}

export function useNoZoomViewport(): void {
  useEffect(() => {
    mountCount += 1;
    if (mountCount === 1) {
      install();
    }

    // `passive: false` is required — a passive listener may not call preventDefault.
    document.addEventListener('gesturestart', blockGesture, { passive: false });
    document.addEventListener('gesturechange', blockGesture, { passive: false });
    document.addEventListener('gestureend', blockGesture, { passive: false });
    document.addEventListener('touchmove', blockPinchTouch, { passive: false });

    return () => {
      document.removeEventListener('gesturestart', blockGesture);
      document.removeEventListener('gesturechange', blockGesture);
      document.removeEventListener('gestureend', blockGesture);
      document.removeEventListener('touchmove', blockPinchTouch);

      mountCount -= 1;
      if (mountCount === 0) {
        uninstall();
      }
    };
  }, []);
}
